import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import type { QuoteOptionSearchResult } from '@techgitter/contracts';
import { useQuoteOptionSearch } from '../../hooks/useQuoteOptionSearch';

interface QuoteOptionSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (result: QuoteOptionSearchResult) => void;
}

const formatCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const trimmed = iso.includes('T') ? iso.split('T')[0] : iso;
  return trimmed;
};

export const QuoteOptionSearchDialog: React.FC<
  QuoteOptionSearchDialogProps
> = ({ open, onClose, onPick }) => {
  const [query, setQuery] = useState('');
  const { results, isLoading, error } = useQuoteOptionSearch(query);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search past quoted options</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Search by description or product code"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching…
            </Typography>
          </Box>
        )}
        {!isLoading && query && results.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            No matches found.
          </Typography>
        )}
        <List dense>
          {results.map((r) => (
            <ListItemButton
              key={`${r.quoteId}-${r.productCode}-${r.description}`}
              onClick={() => onPick(r)}
            >
              <ListItemText
                primary={
                  <>
                    <strong>{r.description || '(no description)'}</strong>
                    {r.productCode && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        {r.productCode}
                      </Typography>
                    )}
                  </>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {r.itemName} · sell {formatCurrency(r.sellPrice)} · buy{' '}
                    {formatCurrency(r.buyPriceExGst)} · {r.quoteNumber}
                    {r.quotedAt ? ` · ${formatDate(r.quotedAt)}` : ''}
                  </Typography>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
